import { create } from 'zustand';

interface FilePreview {
  file: File;
  preview: string;
  base64: string;
}

export interface PlaygroundUserMessage {
  id: string;
  role: 'user';
  content: string;
  file?: FilePreview[];
  panelId?: string; // Add panelId to identify which panel this message belongs to
  payload?: undefined;
}

export interface PlaygroundAssistantMessage {
  id: string;
  role: 'assistant';
  content: string;
  file?: FilePreview[];
  outTokens?: number;
  inTokens?: number;
  startTime?: number;
  endTime?: number;
  panelId?: string; // Add panelId to identify which panel this message belongs to
  error?: string;
  payload?: Record<string, unknown>;
}

export type PlaygroundMessage = PlaygroundUserMessage | PlaygroundAssistantMessage;

interface PlaygroundMessageStore {
  messagesByPanel: Record<string, PlaygroundMessage[]>; // Store messages by panelId
  addMessage: (message: PlaygroundMessage, panelId?: string) => void;
  removeMessage: (messageId: string, panelId?: string) => void;
  resetMessages: (panelId?: string) => void;
  getMessagesForPanel: (panelId: string) => PlaygroundMessage[];
}

const useMessageStore = create<PlaygroundMessageStore>((set, get) => ({
  messagesByPanel: {},

  addMessage: (message, panelId) => {
    const targetPanelId = panelId || message.panelId || 'default';
    set((state) => {
      const panelMessages = state.messagesByPanel[targetPanelId] || [];
      const targetMessageExists = panelMessages.some((m) => m.id === message.id);

      const messageWithPanel = { ...message, panelId: targetPanelId };

      if (targetMessageExists) {
        return {
          messagesByPanel: {
            ...state.messagesByPanel,
            [targetPanelId]: panelMessages.map((m) => {
              return m.id === message.id ? messageWithPanel : m;
            }),
          },
        };
      } else {
        return {
          messagesByPanel: {
            ...state.messagesByPanel,
            [targetPanelId]: [...panelMessages, messageWithPanel],
          },
        };
      }
    });
  },

  removeMessage: (messageId, panelId) => {
    set((state) => {
      if (panelId) {
        // Remove from specific panel
        return {
          messagesByPanel: {
            ...state.messagesByPanel,
            [panelId]: (state.messagesByPanel[panelId] || []).filter((m) => m.id !== messageId),
          },
        };
      } else {
        // Remove from all panels
        const newMessagesByPanel: Record<string, PlaygroundMessage[]> = {};
        Object.keys(state.messagesByPanel).forEach((panel) => {
          newMessagesByPanel[panel] = state.messagesByPanel[panel].filter((m) => m.id !== messageId);
        });
        return { messagesByPanel: newMessagesByPanel };
      }
    });
  },

  resetMessages: (panelId) => {
    set((state) => {
      if (panelId) {
        // Reset specific panel
        return {
          messagesByPanel: {
            ...state.messagesByPanel,
            [panelId]: [],
          },
        };
      } else {
        // Reset all panels
        return { messagesByPanel: {} };
      }
    });
  },

  getMessagesForPanel: (panelId) => {
    return get().messagesByPanel[panelId] || [];
  },
}));

export const resetMessages = (panelId?: string) => {
  useMessageStore.getState().resetMessages(panelId);
};

export const useMessages = (panelId?: string) => {
  return useMessageStore((state) => {
    if (panelId) {
      return state.getMessagesForPanel(panelId);
    }
    // If no panelId provided, return all messages (for backward compatibility)
    return Object.values(state.messagesByPanel).flat();
  });
};

export const addMessage = (message: PlaygroundMessage, panelId?: string) => {
  useMessageStore.getState().addMessage(message, panelId);
};

export const removeMessage = (messageId: string, panelId?: string) => {
  useMessageStore.getState().removeMessage(messageId, panelId);
};

export const getMessageHistory = (panelId?: string) => {
  const state = useMessageStore.getState();
  if (panelId) {
    return state.getMessagesForPanel(panelId);
  }
  return Object.values(state.messagesByPanel).flat();
};
